import { publicQuestionsCount } from './public-question-count';
import type { PublicSubjectCard } from './types';

/**
 * The `?subject=` value for a category's "Other" card: its tests that carry no subject. Reserved, so no
 * real subject can claim it. The API repeats it (`OTHER_SUBJECT` in marketplace-subject.ts).
 */
export const OTHER_SUBJECT = '__other__';

type Range = { min: number; max: number };

/** How long and how big a subject's variants are; a side is null when the API sent nothing usable for it. */
export interface SubjectCardRanges {
  minutes: Range | null;
  questions: Range | null;
}

const rangeOf = (values: number[]): Range | null =>
  values.length > 0 ? { min: Math.min(...values), max: Math.max(...values) } : null;

/**
 * The minutes and question counts across a subject's variants. Questions are counted as each variant's own
 * card counts them (a full IELTS or SAT mock never reads below the real paper).
 */
export function subjectCardRanges(card: Pick<PublicSubjectCard, 'category' | 'variants'>): SubjectCardRanges {
  const variants = card.variants ?? [];
  return {
    minutes: rangeOf(variants.map((variant) => variant.minutes).filter((minutes) => minutes > 0)),
    questions: rangeOf(
      variants.flatMap((variant) => {
        const count = publicQuestionsCount({ category: card.category, questionsCount: variant.questions, sectionsCount: variant.sections });
        return count === undefined ? [] : [count];
      }),
    ),
  };
}

/** "45" for one value, "30–45" for a spread. */
export const formatRange = ({ min, max }: Range): string => (min === max ? `${min}` : `${min}–${max}`);
